import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Modal, Skeleton } from '../../components/ui';
import { Icon, type IconName } from '../../lib/icons';
import {
  useMorningBriefIcon,
  useMorningBriefToday,
  useMorningBriefPreferences,
  useMorningBriefActions,
} from './api';
import {
  BriefSummaryHeader,
  BriefSectionBlock,
  SECTION_ORDER,
  groupItems,
  greeting,
} from './ui';
import { getActiveUser } from '../../lib/api';

const LAST_SEEN_KEY = 'atlas.morningBrief.lastSeen';
const todayKey = () => `${getActiveUser()}::${new Date().toDateString()}`;

/**
 * The Morning Brief header widget: a top-right icon (state-driven badge), a
 * right-side drawer preview, and the once-per-day first-login popup. Rendered
 * once in the AppShell header.
 */
export function MorningBriefWidget() {
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const popupHandled = useRef(false);

  const { data: icon } = useMorningBriefIcon();
  const { data: prefsData } = useMorningBriefPreferences();
  const prefs = prefsData?.preferences;
  // Today's detail is only needed when the drawer/popup is open.
  const { data: todayData, isLoading: todayLoading } = useMorningBriefToday(drawerOpen || popupOpen);
  const brief = todayData?.brief ?? null;
  const { markRead, dismiss, markPopupShown } = useMorningBriefActions();

  const enabled = prefs?.enabled !== false;
  const showIcon = enabled && prefs?.showTopRightIcon !== false;

  // First-login-of-day popup: open once when an unopened brief exists today.
  useEffect(() => {
    if (popupHandled.current || !icon || !prefs) return;
    if (prefs.enabled === false || prefs.showOnFirstLogin === false) return;
    const ready = icon.briefId && icon.state !== 'failed' && icon.state !== 'loading' && icon.state !== 'none';
    if (ready && icon.hasUnopened && localStorage.getItem(LAST_SEEN_KEY) !== todayKey()) {
      popupHandled.current = true;
      setPopupOpen(true);
      if (icon.briefId) markPopupShown.mutate(icon.briefId);
    }
  }, [icon, prefs, markPopupShown]);

  const markSeenToday = () => localStorage.setItem(LAST_SEEN_KEY, todayKey());

  const onDismissPopup = () => {
    markSeenToday();
    if (icon?.briefId) dismiss.mutate(icon.briefId);
    setPopupOpen(false);
  };
  const onOpenFull = () => {
    markSeenToday();
    if (icon?.briefId) markRead.mutate(icon.briefId);
    setPopupOpen(false);
    setDrawerOpen(false);
    nav('/morning-brief');
  };

  if (!showIcon) return null;

  const state = icon?.state ?? 'none';
  const badge = icon?.badge ?? 0;
  const badgeTone =
    state === 'critical' ? 'bg-rose-600' : state === 'important' ? 'bg-amber-500' : 'bg-brand';
  const glyph: IconName = state === 'failed' ? 'warning' : state === 'loading' ? 'pending' : 'morning-brief';

  return (
    <>
      <button
        className="btn-ghost relative"
        onClick={() => setDrawerOpen(true)}
        title={state === 'failed' ? 'Morning Brief — generation failed' : 'Morning Brief'}
      >
        <Icon name={glyph} />
        {state !== 'failed' && state !== 'loading' && badge > 0 && (
          <span className={`absolute -top-1 -right-1 ${badgeTone} text-white text-[10px] leading-none rounded-full px-1 py-0.5 min-w-[16px] text-center`}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        {state === 'unread' && badge === 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-brand" />
        )}
      </button>

      {/* Right-side drawer preview */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
            />
            <motion.aside
              className="fixed right-0 top-0 z-50 h-full w-full max-w-md card rounded-none border-l flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-1.5"><Icon name="morning-brief" /> Morning Brief</h3>
                <button className="text-muted hover:text-ink text-lg" onClick={() => setDrawerOpen(false)}><Icon name="close" /></button>
              </div>
              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                {todayLoading ? (
                  <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}</div>
                ) : !brief ? (
                  <BriefFallback onGenerate={() => nav('/morning-brief')} state={state} />
                ) : (
                  <>
                    <BriefSummaryHeader brief={brief} />
                    {SECTION_ORDER.filter((s) => ['needs_attention', 'recommended_focus', 'mention_comment', 'mention_document'].includes(s)).map((section) => {
                      const items = groupItems(brief.items).get(section) ?? [];
                      if (items.length === 0) return null;
                      return (
                        <BriefSectionBlock
                          key={section}
                          section={section}
                          items={items}
                          compact
                          onNavigate={() => setDrawerOpen(false)}
                        />
                      );
                    })}
                  </>
                )}
              </div>
              <div className="border-t border-border p-3 flex items-center justify-between gap-2">
                <button className="btn-ghost text-xs" onClick={() => { setDrawerOpen(false); nav('/morning-brief?tab=history'); }}>
                  History
                </button>
                <button className="btn-ghost text-xs" onClick={() => { setDrawerOpen(false); nav('/morning-brief?tab=preferences'); }}>
                  Preferences
                </button>
                <button
                  className="btn-brand text-xs"
                  onClick={() => { if (brief) markRead.mutate(brief.id); setDrawerOpen(false); nav('/morning-brief'); }}
                >
                  Open full brief
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* First-login popup */}
      <Modal open={popupOpen} onClose={onDismissPopup} title={<><Icon name="morning-brief" /> {greeting()}{userFirstName()}</>} wide>
        {todayLoading ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}</div>
        ) : !brief ? (
          <BriefFallback onGenerate={() => { setPopupOpen(false); nav('/morning-brief'); }} state={state} />
        ) : (
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <p className="text-sm text-muted">Here's what changed since yesterday and what needs your attention today.</p>
            <BriefSummaryHeader brief={brief} />
            {SECTION_ORDER.filter((s) => ['needs_attention', 'recommended_focus', 'mention_comment', 'mention_document', 'assigned_work'].includes(s)).map((section) => {
              const items = groupItems(brief.items).get(section) ?? [];
              if (items.length === 0) return null;
              return <BriefSectionBlock key={section} section={section} items={items} compact onNavigate={() => setPopupOpen(false)} />;
            })}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 mt-5 pt-3 border-t border-border">
          <button className="btn-ghost" onClick={onDismissPopup}>Dismiss</button>
          <button className="btn-brand" onClick={onOpenFull}>Open full brief</button>
        </div>
      </Modal>
    </>
  );
}

function userFirstName(): string {
  // Dev session identity is an email; show the local-part's first token.
  const local = getActiveUser().split('@')[0] ?? '';
  const name = local.split(/[._-]/)[0];
  return name ? `, ${name.charAt(0).toUpperCase()}${name.slice(1)}` : '';
}

function BriefFallback({ onGenerate, state }: { onGenerate: () => void; state: string }) {
  return (
    <div className="text-center py-8 space-y-3">
      <div className="text-3xl opacity-50"><Icon name={state === 'failed' ? 'warning' : 'morning-brief'} /></div>
      <p className="text-sm text-ink font-medium">
        {state === 'failed' ? "Today's brief failed to generate." : 'Your Morning Brief is being prepared.'}
      </p>
      <p className="text-xs text-muted">
        {state === 'failed'
          ? 'You can try generating it again.'
          : 'It will appear here shortly, or you can generate it now.'}
      </p>
      <button className="btn-brand text-sm" onClick={onGenerate}>Open Morning Brief</button>
    </div>
  );
}
